/*
 * Proprietary and Confidential
 *
 * Copyright (c) [2018] -  [] Marcelo H. Krebber - Munich, London 2018
 * All Rights Reserved.
 *
 * Dissemination or reproduction of this file [dropzone-uploader.js] or parts within
 * via any medium is strictly forbidden unless prior written permission is obtained
 * from <marcelo.krebber@gmail.com>
 *
 * Last modified: 27.02.20, 14:09
 */

/**
 * file and image uploader with compression
 */

import axios from "axios";
import {authConfig} from "../../actions/local-storage";
import {getVideoThumbnail} from "../media-players/create-video-thumbnail";

// max.parameters for uploading images with compression
const MAX_WIDTH = 2000;
const MAX_HEIGHT = 1000;
const MIME_TYPE = "image/jpeg";
const QUALITY = 0.8;
const MAX_FILE_SIZE_UPLOAD = 480 * 1000; // 480MB

function calculateSize(img, maxWidth, maxHeight) {
    let width = img.width;
    let height = img.height;

    // calculate the width and height, constraining the proportions
    if (width > height) {
        if (width > maxWidth) {
            height = Math.round((height * maxWidth) / width);
            width = maxWidth;
        }
    } else {
        if (height > maxHeight) {
            width = Math.round((width * maxHeight) / height);
            height = maxHeight;
        }
    }
    return [width, height];
}

export function isSupportedVideoType(type) {
    return type === 'video/mp4' || type === 'video/mpeg';
}

// currently small images might result in a bigger file after compressing
// optional for refactoring: check on file size before compression
export function compressToBlog(file, callback) {

    const blobURL = URL.createObjectURL(file);
    const img = new Image();
    img.src = blobURL;
    img.onerror = function () {
        URL.revokeObjectURL(this.src);
        // Handle the failure properly
        console.log("Cannot load image", file);
    };

    img.onload = function () {
        URL.revokeObjectURL(this.src);
        const [newWidth, newHeight] = calculateSize(img, MAX_WIDTH, MAX_HEIGHT);
        const canvas = document.createElement("canvas");
        canvas.width = newWidth;
        canvas.height = newHeight;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, newWidth, newHeight);
        canvas.toBlob((blob) => {
                callback && callback(blob);
            },
            MIME_TYPE,
            QUALITY
        );
    }
}

function getFormData({file, text='', filename='', thumbnail='', height=0, width=0, size=0}) {
    const formData = new FormData();
    if (filename) {
        formData.append("file", file, filename);
    } else formData.append("file", file);
    formData.append("thumbnail", thumbnail);
    formData.append("text", text);
    formData.append("type", file.type);
    formData.append("height", height);
    formData.append("width", width);
    formData.append("size", size);

    return formData;
}

// workhorse for uploading images to the server
// returns spaceMedia - uploaded media ie files
// caller must await this function
// export async function uploadFilesAsyncWorker(username, mediapath, files) {
export async function uploadFilesAsyncWorker(mediaUploadUrl, files, text = '') {
    const media = [];

    /* keep ordering of the files array as in idx */
    const upLoaders = files.map((file, idx) => {

        // if this is a video file upload preview thumbnail additionally
        if (isSupportedVideoType(file.type)) {
            return new Promise(async (resolve) => {
                let thumbMedia;

                // create a thumbnail of the video / height and width same as video
                const {blob, height, width} = await getVideoThumbnail(file);

                // load thumbnail
                const filename = file.name.substr(0, file.name.lastIndexOf('.'));
                let formData = getFormData({
                    file: blob,
                    text: text,
                    filename: `${filename}_thumb.jpeg`,
                    height: height, width: width, size: blob.size});

                try {
                    await axios.post(mediaUploadUrl, formData, authConfig())
                    .then(response => {
                        thumbMedia = response.data;
                    });
                } catch (error) {
                    console.log(error)
                }

                // load video file
                formData = getFormData({
                    file:file,
                    text:text,
                    thumbnail: thumbMedia.url,
                    height: height, width: width, size: file.size});

                await axios.post(mediaUploadUrl, formData, authConfig())
                .then(response => {
                    const uploaded = response.data;
                    uploaded.position = idx;
                    media.push(uploaded);
                    resolve(true);
                });
            })

            // small file size OK to upload
            // no need to compress every image if size below max threshold
        } else if (file.size < MAX_FILE_SIZE_UPLOAD) {
            return new Promise(async (resolve) => {
                const formData = getFormData({
                    file: file,
                    text: text,
                    size: file.size});

                await axios.post(mediaUploadUrl, formData, authConfig())
                .then(response => {
                    const uploaded = response.data;
                    uploaded.position = idx;
                    media.push(uploaded);
                    resolve(true);
                });
            })

            // big file - compress before uploading
        } else {
            return new Promise((resolve) => {
                compressToBlog(file, async blob => {
                    const formData = getFormData({
                        file: blob,
                        text: text,
                        filename: file.name,
                        size: blob.size});

                    await axios.post(mediaUploadUrl, formData, authConfig())
                    .then(response => {
                        const uploaded = response.data;
                        uploaded.position = idx;
                        media.push(uploaded);

                        // resolve when image compressed and uploaded
                        resolve(true);
                    });
                })
            })
        }
    });

    // wait for all images to upload
    await Promise.all(upLoaders);

    // return array of final uploaded images
    return media;
}
